import { useEffect, useMemo, useState } from 'react';
import { uploadTemplateHeaderSample } from '../api/waMedia.js';

/**
 * #162 — Builder visual para `components[]` de templates Meta/360dialog.
 *
 * Reemplaza al textarea/Monaco crudo cuando el admin no quiere editar JSON.
 *
 * Convención Meta Graph (`POST /{waba_id}/message_templates`):
 *   {
 *     "name": "<templateName>",
 *     "language": "es_CL",
 *     "category": "UTILITY",
 *     "components": [
 *       { "type": "HEADER", "format": "TEXT", "text": "Hola {{1}}" },
 *       { "type": "BODY", "text": "Cuerpo {{1}}" },
 *       { "type": "FOOTER", "text": "Equipo Zerviz" },
 *       { "type": "BUTTONS", "buttons": [ ... ] }
 *     ]
 *   }
 *
 * Soportamos los tipos más usados; el resto (carousel, LTO, coupons) sigue
 * disponible vía toggle a "JSON raw".
 */

export type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
export type ButtonKind = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

export interface BuilderState {
  header: {
    format: HeaderFormat;
    text?: string;
    /** Para IMAGE/VIDEO/DOCUMENT: media_id de Meta o sample URL público. */
    example?: string;
  };
  body: {
    text: string;
    /** Ejemplos por placeholder (Meta los pide en `example.body_text`). */
    examples: string[];
  };
  footer: {
    text: string;
  };
  buttons: Array<
    | { kind: 'QUICK_REPLY'; text: string }
    | { kind: 'URL'; text: string; url: string }
    | { kind: 'PHONE_NUMBER'; text: string; phone_number: string }
  >;
}

export const EMPTY_BUILDER: BuilderState = {
  header: { format: 'NONE' },
  body: { text: '', examples: [] },
  footer: { text: '' },
  buttons: [],
};

/**
 * Conversión BuilderState → components[] (Meta payload-ready).
 * Omite secciones vacías para no romper validación Meta.
 */
export function builderToComponents(s: BuilderState): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];

  if (s.header.format !== 'NONE') {
    const h: Record<string, unknown> = { type: 'HEADER', format: s.header.format };
    if (s.header.format === 'TEXT' && s.header.text) {
      h['text'] = s.header.text;
    } else if (s.header.example) {
      // #179 — header_handle lleva el HANDLE de la Resumable Upload API, no un
      // media_id ni una URL: Meta rechaza el template si viene cualquier otra cosa.
      h['example'] = { header_handle: [s.header.example] };
    }
    out.push(h);
  }

  if (s.body.text) {
    const b: Record<string, unknown> = { type: 'BODY', text: s.body.text };
    if (s.body.examples.length > 0) {
      b['example'] = { body_text: [s.body.examples] };
    }
    out.push(b);
  }

  if (s.footer.text) {
    out.push({ type: 'FOOTER', text: s.footer.text });
  }

  if (s.buttons.length > 0) {
    const buttons = s.buttons.map((b) => {
      switch (b.kind) {
        case 'QUICK_REPLY':
          return { type: 'QUICK_REPLY', text: b.text };
        case 'URL':
          return { type: 'URL', text: b.text, url: b.url };
        case 'PHONE_NUMBER':
          return { type: 'PHONE_NUMBER', text: b.text, phone_number: b.phone_number };
      }
    });
    out.push({ type: 'BUTTONS', buttons });
  }

  return out;
}

/** Conversión inversa: components[] → BuilderState (best-effort). */
export function componentsToBuilder(arr: Array<Record<string, unknown>>): BuilderState {
  const s: BuilderState = JSON.parse(JSON.stringify(EMPTY_BUILDER)) as BuilderState;
  for (const c of arr) {
    const type = String(c['type'] ?? '').toUpperCase();
    if (type === 'HEADER') {
      s.header.format = (String(c['format'] ?? 'TEXT') as HeaderFormat) || 'TEXT';
      if (typeof c['text'] === 'string') s.header.text = c['text'];
      const ex = c['example'] as { header_handle?: string[] } | undefined;
      if (ex?.header_handle?.[0]) s.header.example = ex.header_handle[0];
    } else if (type === 'BODY') {
      if (typeof c['text'] === 'string') s.body.text = c['text'];
      const ex = c['example'] as { body_text?: string[][] } | undefined;
      if (ex?.body_text?.[0]) s.body.examples = ex.body_text[0];
    } else if (type === 'FOOTER') {
      if (typeof c['text'] === 'string') s.footer.text = c['text'];
    } else if (type === 'BUTTONS' && Array.isArray(c['buttons'])) {
      const bs = c['buttons'] as Array<Record<string, unknown>>;
      s.buttons = bs.flatMap((b): BuilderState['buttons'] => {
        const bt = String(b['type'] ?? '').toUpperCase();
        const text = String(b['text'] ?? '');
        if (bt === 'QUICK_REPLY') {
          return [{ kind: 'QUICK_REPLY', text }];
        }
        if (bt === 'URL') {
          return [{ kind: 'URL', text, url: String(b['url'] ?? '') }];
        }
        if (bt === 'PHONE_NUMBER') {
          return [{
            kind: 'PHONE_NUMBER', text,
            phone_number: String(b['phone_number'] ?? ''),
          }];
        }
        return [];
      });
    }
  }
  return s;
}

interface Props {
  value: BuilderState;
  onChange: (next: BuilderState) => void;
  /** #179 — necesarios para subir el archivo de muestra del header. */
  tenantId?: string;
  connectorId?: string;
}

export function TemplateComponentsBuilder({ value, onChange, tenantId, connectorId }: Props): JSX.Element {
  // Extraemos placeholders de body.text para auto-poblar examples.
  const placeholders = useMemo(
    () => Array.from(value.body.text.matchAll(/\{\{(\d+)\}\}/g))
      .map((m) => Number(m[1])).sort((a, b) => a - b),
    [value.body.text],
  );

  // Si el número de placeholders cambia, ajustamos examples.
  useEffect(() => {
    const want = placeholders.length;
    if (value.body.examples.length !== want) {
      const next = [...value.body.examples];
      while (next.length < want) next.push('');
      next.length = want;
      onChange({ ...value, body: { ...value.body, examples: next } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholders.length]);

  return (
    <div className="space-y-4 rounded border border-slate-200 bg-slate-50 p-4">
      <HeaderSection value={value} onChange={onChange} tenantId={tenantId} connectorId={connectorId} />
      <BodySection value={value} onChange={onChange} placeholders={placeholders} />
      <FooterSection value={value} onChange={onChange} />
      <ButtonsSection value={value} onChange={onChange} />
    </div>
  );
}

function HeaderSection({ value, onChange, tenantId, connectorId }: Props): JSX.Element {
  const { format } = value.header;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  return (
    <fieldset className="rounded border border-slate-200 bg-white p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Header</legend>
      <select
        value={format}
        onChange={(e) =>
          onChange({ ...value, header: { format: e.target.value as HeaderFormat } })
        }
        className="w-full md:w-64 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
      >
        <option value="NONE">Sin header</option>
        <option value="TEXT">Texto</option>
        <option value="IMAGE">Imagen</option>
        <option value="VIDEO">Video</option>
        <option value="DOCUMENT">Documento</option>
      </select>
      {format === 'TEXT' && (
        <input
          type="text"
          maxLength={60}
          placeholder="Texto del header (max 60, soporta {{1}})"
          value={value.header.text ?? ''}
          onChange={(e) => onChange({ ...value, header: { ...value.header, text: e.target.value } })}
          className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm"
        />
      )}
      {(format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT') && (
        <div className="mt-2 space-y-1">
          {/*
            #179 — Meta NO acepta un media_id ni una URL acá. Para crear un
            template con header multimedia exige el handle de un archivo de
            MUESTRA subido por la Resumable Upload API. Antes esto era un campo
            de texto libre y el template se rechazaba al publicarlo.
          */}
          <input
            type="file"
            accept={
              format === 'IMAGE'
                ? 'image/png,image/jpeg'
                : format === 'VIDEO'
                  ? 'video/mp4'
                  : 'application/pdf'
            }
            disabled={uploading || !tenantId || !connectorId}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (!file || !tenantId || !connectorId) return;
              setUploading(true);
              setUploadError(null);
              uploadTemplateHeaderSample(tenantId, connectorId, file)
                .then((res) => {
                  onChange({ ...value, header: { ...value.header, example: res.handle } });
                })
                .catch((err: unknown) => {
                  setUploadError(err instanceof Error ? err.message : 'Error al subir la muestra');
                })
                .finally(() => setUploading(false));
            }}
            className="w-full text-xs"
          />
          <p className="text-xs text-slate-500">
            Meta exige un archivo de ejemplo para aprobar el template. No es el
            que se envía después: es sólo la muestra que revisa el equipo de Meta.
          </p>
          {!connectorId && (
            <p className="text-xs text-amber-700">Elegí un connector antes de subir la muestra.</p>
          )}
          {uploading && <p className="text-xs text-slate-600">Subiendo muestra a Meta…</p>}
          {uploadError && <p className="text-xs text-red-700">{uploadError}</p>}
          {value.header.example && !uploading && (
            <p className="font-mono text-xs text-green-700" data-testid="header-handle">
              handle: {value.header.example.slice(0, 24)}…
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}

function BodySection({
  value, onChange, placeholders,
}: Props & { placeholders: number[] }): JSX.Element {
  return (
    <fieldset className="rounded border border-slate-200 bg-white p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Body</legend>
      <textarea
        rows={4}
        maxLength={1024}
        placeholder="Cuerpo del template. Usá {{1}}, {{2}} para placeholders."
        value={value.body.text}
        onChange={(e) => onChange({ ...value, body: { ...value.body, text: e.target.value } })}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>{value.body.text.length}/1024 chars</span>
        <span>Placeholders detectados: {placeholders.join(', ') || 'ninguno'}</span>
      </div>
      {placeholders.length > 0 && (
        <div className="mt-2 space-y-1">
          <span className="text-xs font-medium text-slate-700">Ejemplos (Meta los exige):</span>
          {placeholders.map((idx, i) => (
            <input
              key={idx}
              type="text"
              placeholder={`Ejemplo para {{${idx}}}`}
              value={value.body.examples[i] ?? ''}
              onChange={(e) => {
                const next = [...value.body.examples];
                next[i] = e.target.value;
                onChange({ ...value, body: { ...value.body, examples: next } });
              }}
              className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
          ))}
        </div>
      )}
    </fieldset>
  );
}

function FooterSection({ value, onChange }: Props): JSX.Element {
  return (
    <fieldset className="rounded border border-slate-200 bg-white p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Footer (opcional)</legend>
      <input
        type="text"
        maxLength={60}
        placeholder="Texto del footer (max 60, sin placeholders)"
        value={value.footer.text}
        onChange={(e) => onChange({ ...value, footer: { text: e.target.value } })}
        className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
      />
    </fieldset>
  );
}

function ButtonsSection({ value, onChange }: Props): JSX.Element {
  const add = (kind: ButtonKind): void => {
    if (value.buttons.length >= 3) return;
    const next = [...value.buttons];
    if (kind === 'QUICK_REPLY') next.push({ kind, text: '' });
    else if (kind === 'URL') next.push({ kind, text: '', url: '' });
    else next.push({ kind, text: '', phone_number: '' });
    onChange({ ...value, buttons: next });
  };
  const remove = (i: number): void => {
    onChange({ ...value, buttons: value.buttons.filter((_, idx) => idx !== i) });
  };
  const updateAt = <T extends BuilderState['buttons'][number]>(i: number, b: T): void => {
    const next = [...value.buttons];
    next[i] = b;
    onChange({ ...value, buttons: next });
  };

  return (
    <fieldset className="rounded border border-slate-200 bg-white p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-slate-600">
        Buttons (max 3)
      </legend>
      <div className="mb-2 flex gap-1">
        <button type="button" onClick={() => add('QUICK_REPLY')}
          disabled={value.buttons.length >= 3}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50">
          + Quick Reply
        </button>
        <button type="button" onClick={() => add('URL')}
          disabled={value.buttons.length >= 3}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50">
          + URL
        </button>
        <button type="button" onClick={() => add('PHONE_NUMBER')}
          disabled={value.buttons.length >= 3}
          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50">
          + Phone
        </button>
      </div>
      <div className="space-y-2">
        {value.buttons.map((b, i) => (
          <div key={i} className="flex gap-2 items-start rounded border border-slate-100 bg-slate-50 p-2">
            <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-mono">{b.kind}</span>
            <div className="flex-1 space-y-1">
              <input
                type="text"
                placeholder="Texto del botón (max 25)"
                maxLength={25}
                value={b.text}
                onChange={(e) => updateAt(i, { ...b, text: e.target.value })}
                className="w-full rounded border border-slate-300 px-2 py-0.5 text-xs"
              />
              {b.kind === 'URL' && (
                <input
                  type="url"
                  placeholder="https://… (puede contener {{1}})"
                  value={b.url}
                  onChange={(e) => updateAt(i, { ...b, url: e.target.value })}
                  className="w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
                />
              )}
              {b.kind === 'PHONE_NUMBER' && (
                <input
                  type="tel"
                  placeholder="+56912345678"
                  value={b.phone_number}
                  onChange={(e) => updateAt(i, { ...b, phone_number: e.target.value })}
                  className="w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
                />
              )}
            </div>
            <button type="button" onClick={() => remove(i)}
              className="text-red-700 text-xs hover:underline" aria-label="Quitar">
              ✕
            </button>
          </div>
        ))}
        {value.buttons.length === 0 && (
          <p className="text-xs text-slate-500">Sin botones.</p>
        )}
      </div>
    </fieldset>
  );
}

// ── Hook auxiliar ─────────────────────────────────────────────────────────
/**
 * Mantiene un BuilderState sincronizado con un string JSON externo.
 * - Init: parsea el JSON si es válido y popula el builder.
 * - Push: cuando el builder cambia, emite el JSON nuevo.
 */
export function useTemplateComponentsBuilder(
  jsonString: string,
  onJsonChange: (next: string) => void,
): { builder: BuilderState; setBuilder: (s: BuilderState) => void } {
  const [builder, setBuilderState] = useState<BuilderState>(() => {
    try {
      const arr = JSON.parse(jsonString || '[]');
      return Array.isArray(arr) ? componentsToBuilder(arr) : EMPTY_BUILDER;
    } catch { return EMPTY_BUILDER; }
  });

  const setBuilder = (s: BuilderState): void => {
    setBuilderState(s);
    const arr = builderToComponents(s);
    onJsonChange(arr.length > 0 ? JSON.stringify(arr, null, 2) : '');
  };

  return { builder, setBuilder };
}
